// Collects per-token / per-horizon backend notification strings out of a
// run_risk_engine result map so the risk page can surface them in the
// "Something went wrong" alert (see BookErrorsMessage).

export const formatPairLabel = (pairKey: string) => {
  if (!pairKey) return pairKey;
  if (pairKey.includes('-')) return pairKey;
  return pairKey.length > 3 ? `${pairKey.slice(0, -3)}-${pairKey.slice(-3)}` : pairKey;
};

// IM-2725: sticky-delta (DELTA(STICKY_DELTA)) is only computed at the current
// horizon (0D). At future horizons the backend returns valid results PLUS a
// soft per-horizon notification, e.g.
//   "DELTA(STICKY_DELTA) is calculated at T_0 only - not reported for horizon 1D."
// William's call: keep requesting sticky-delta unchanged and simply hide these
// soft messages from the user. This is a display-only filter on the collected
// notifications, not a change to the request. Any other notification still
// shows. Case-insensitive match on the word "STICKY".
const containsSticky = (notification: unknown): boolean => {
  const text = typeof notification === 'string' ? notification : JSON.stringify(notification);
  return typeof text === 'string' && text.toLowerCase().includes('sticky');
};

export const collectNotificationsFromTokenMap = (
  tokenMap: any,
): Array<Record<string, string[]>> => {
  if (!tokenMap || typeof tokenMap !== 'object') return [];
  const errors: Array<Record<string, string[]>> = [];

  Object.entries(tokenMap).forEach(([tokenKey, tokenEntry]: [string, any]) => {
    let notifications: string[] = [];

    if (tokenEntry) {
      if (Array.isArray((tokenEntry as any).notifications)) {
        notifications = (tokenEntry as any).notifications as string[];
      } else if (Array.isArray(tokenEntry)) {
        for (const item of tokenEntry as any[]) {
          if (Array.isArray((item as any)?.notifications)) {
            notifications = notifications.concat((item as any).notifications as string[]);
          } else if (Array.isArray((item as any)?.report?.notifications)) {
            notifications = notifications.concat((item as any).report.notifications as string[]);
          }
        }
      }
    }

    // IM-2725: drop sticky-delta future-horizon notifications; keep the rest.
    notifications = notifications.filter((n) => !containsSticky(n));

    if (notifications.length > 0) {
      errors.push({ [formatPairLabel(tokenKey)]: notifications });
    }
  });

  return errors;
};
